import React, { useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Typography from "@mui/material/Typography";
import GridContainer from "components/Grid/GridContainer.js";
import GridItem from "components/Grid/GridItem.js";
import Button from "components/CustomButtons/Button.js";
import Card from "components/Card/Card.js";
import CardBody from "components/Card/CardBody.js";
import CardFooter from "components/Card/CardFooter.js";
import CustomInput from "components/CustomInput/CustomInput.js";
import SimpleAlert from "../../components/AtutaAlerts/CustomAlerts";
import CircularProgress from "@material-ui/core/CircularProgress";
import PersonAddIcon from "@material-ui/icons/PersonAdd";

import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";

import { useHistory } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";

import AuthApi from "../../api_calls/AuthApi";
import ProfilesApi from "../../api_calls/ApiServices";

import loginPageStyles from "assets/jss/material-dashboard-pro-react/views/loginPageStyle.js";

const MAX_PHONE_LENGTH = 16;
const MAX_EMAIL_LENGTH = 120;

const useStyles = makeStyles((theme) => ({
  ...loginPageStyles,
  errorText: {
    color: theme.palette.error.main || "red",
    fontSize: "12px",
    marginTop: "5px",
    textAlign: "left",
    paddingLeft: "15px",
  },
}));

export default function CreateTLMarketer() {
  const classes = useStyles();
  const history = useHistory();
  const { user: authUser } = useAuth();

  const [firstName, setFirstName] = useState("");
  const [otherNames, setOtherNames] = useState("");
  const [pseudoName, setPseudoName] = useState("");
  const [upline, setUpline] = useState("");
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [country, setCountry] = useState("");
  const [countries, setCountries] = useState([]);

  const [loading, setLoading] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [errors, setErrors] = useState({});
  const [openDialog, setOpenDialog] = useState(false);
  const [success, setSuccess] = useState(false);

  // -----------------------
  // AUTO SET UPLINE
  // -----------------------
  useEffect(() => {
    if (authUser?.account) {
      setUpline(authUser.account);
    }
  }, [authUser]);

  // -----------------------
  // LOAD COUNTRIES
  // -----------------------
  useEffect(() => {
    const loadCountries = async () => {
      try {
        const countryData = await ProfilesApi.fetchCountries();
        setCountries(countryData);
      } catch {
        console.error("Failed to load countries");
      }
    };
    loadCountries();
  }, []);

  // -----------------------
  // HELPERS
  // -----------------------
  const isValidEmail = (val) => /\S+@\S+\.\S+/.test(val);

  const handlePhoneChange = (e) => {
    let value = e.target.value.replace(/(?!^\+)[^\d]/g, "");
    if (value.length > MAX_PHONE_LENGTH) {
      value = value.slice(0, MAX_PHONE_LENGTH);
    }
    setPhoneNumber(value);
    setErrors((p) => ({ ...p, phoneNumber: "" }));
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    if (value.length <= MAX_EMAIL_LENGTH) {
      setEmail(value);
      setErrors((p) => ({ ...p, email: "" }));
    }
  };

  // -----------------------
  // VALIDATION
  // -----------------------
  const validate = () => {
    const errs = {};

    if (!firstName.trim()) errs.firstName = "First name is required.";
    if (!otherNames.trim()) errs.otherNames = "Other names are required.";
    if (!pseudoName.trim()) errs.pseudoName = "Pseudo name is required.";

    if (!email.trim()) errs.email = "Email is required.";
    else if (!isValidEmail(email)) errs.email = "Enter a valid email address.";

    if (!phoneNumber.trim()) errs.phoneNumber = "Phone number is required.";
    else if (!/^\+?\d+$/.test(phoneNumber))
      errs.phoneNumber = "Only numbers and + are allowed.";

    if (!country) errs.country = "Please select a country.";

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // -----------------------
  // SUBMIT
  // -----------------------
  const handleSubmit = async (e) => {
    e.preventDefault();
    setAlertMessage("");
    setErrors({});

    if (!validate()) return;

    setLoading(true);
    try {
      const response = await AuthApi.createStaff({
        firstName,
        otherNames,
        pseudoName,
        upline,
        email,
        phoneNumber,
        country,
        userrole: "4",
      });

      if (response.status === "success") {
        setSuccess(true);
        setOpenDialog(true);

        setFirstName("");
        setOtherNames("");
        setPseudoName("");
        setEmail("");
        setPhoneNumber("");
        setCountry("");
      } else {
        setAlertMessage(
          response.data?.message || "Failed to create TL marketer.",
        );
      }
    } catch {
      setAlertMessage("Server error. Failed to create TL marketer.");
    } finally {
      setLoading(false);
    }
  };

  // -----------------------
  // UI
  // -----------------------
  return (
    <div>
      {!success && (
        <GridContainer justify="left">
          <GridItem xs={12} sm={7} md={6}>
            <Typography
              sx={{
                fontWeight: "normal",
                fontSize: "15px",
                fontFamily: "Calibri, sans-serif",
                display: "flex",
                alignItems: "center",
                gap: 1,
                marginBottom: "10px",
              }}
            >
              <PersonAddIcon /> Create TL Marketer
            </Typography>

            <form onSubmit={handleSubmit}>
              <Card>
                <CardBody>
                  <CustomInput
                    labelText="First Name"
                    formControlProps={{ fullWidth: true }}
                    inputProps={{
                      value: firstName,
                      onChange: (e) => {
                        setFirstName(e.target.value);
                        setErrors((p) => ({ ...p, firstName: "" }));
                      },
                    }}
                  />
                  {errors.firstName && (
                    <p className={classes.errorText}>{errors.firstName}</p>
                  )}

                  <CustomInput
                    labelText="Other Names"
                    formControlProps={{ fullWidth: true }}
                    inputProps={{
                      value: otherNames,
                      onChange: (e) => {
                        setOtherNames(e.target.value);
                        setErrors((p) => ({ ...p, otherNames: "" }));
                      },
                    }}
                  />
                  {errors.otherNames && (
                    <p className={classes.errorText}>{errors.otherNames}</p>
                  )}

                  <CustomInput
                    labelText="Pseudo Name"
                    formControlProps={{ fullWidth: true }}
                    inputProps={{
                      value: pseudoName,
                      onChange: (e) => {
                        setPseudoName(e.target.value);
                        setErrors((p) => ({ ...p, pseudoName: "" }));
                      },
                    }}
                  />
                  {errors.pseudoName && (
                    <p className={classes.errorText}>{errors.pseudoName}</p>
                  )}

                  <CustomInput
                    labelText="Referred By"
                    formControlProps={{ fullWidth: true }}
                    inputProps={{ value: upline, disabled: true }}
                  />

                  <CustomInput
                    labelText="Email"
                    formControlProps={{ fullWidth: true }}
                    inputProps={{ value: email, onChange: handleEmailChange }}
                  />
                  {errors.email && (
                    <p className={classes.errorText}>{errors.email}</p>
                  )}

                  <CustomInput
                    labelText="Phone Number"
                    formControlProps={{ fullWidth: true }}
                    inputProps={{
                      value: phoneNumber,
                      onChange: handlePhoneChange,
                      inputMode: "tel",
                      maxLength: MAX_PHONE_LENGTH,
                    }}
                  />
                  {errors.phoneNumber && (
                    <p className={classes.errorText}>{errors.phoneNumber}</p>
                  )}

                  <select
                    style={{
                      width: "100%",
                      padding: "10px",
                      marginTop: "15px",
                    }}
                    value={country}
                    onChange={(e) => {
                      setCountry(e.target.value);
                      setErrors((p) => ({ ...p, country: "" }));
                    }}
                  >
                    <option value="">Select country</option>
                    {countries.map((c) => (
                      <option key={c.isoCode} value={c.isoCode}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                  {errors.country && (
                    <p className={classes.errorText}>{errors.country}</p>
                  )}
                </CardBody>

                <CardFooter className={classes.justifyContentCenter}>
                  <Button color="rose" type="submit" disabled={loading}>
                    {loading ? (
                      <CircularProgress size={24} style={{ color: "#fff" }} />
                    ) : (
                      "Create TL Marketer"
                    )}
                  </Button>
                </CardFooter>
              </Card>

              {alertMessage && (
                <SimpleAlert
                  message={alertMessage}
                  onClose={() => setAlertMessage("")}
                />
              )}
            </form>
          </GridItem>
        </GridContainer>
      )}

      {/* SUCCESS DIALOG */}
      <Dialog open={openDialog} disableBackdropClick disableEscapeKeyDown>
        <DialogTitle>Success</DialogTitle>
        <DialogContent>
          <Typography>
            TL Marketer account has been created successfully.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            color="rose"
            onClick={() => {
              setOpenDialog(false);
              setSuccess(false);
              history.push("/singles/create-tl-marketer");
            }}
          >
            Add another
          </Button>
          <Button
            color="info"
            onClick={() => {
              setOpenDialog(false);
              history.push("/");
            }}
          >
            Home
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
